import { memo, useEffect, useMemo, useRef } from 'react';

import cn from 'classnames';
import {
    ColorType,
    createChart,
    type IChartApi,
    type Time,
} from 'lightweight-charts';

import { THEME } from '@/shared/constants/theme';
import { useLanguage, useTheme } from '@/shared/lib/hooks';
import { Loader } from '@/shared/ui/loader';

import type { TradingPlatformAccountStatsChartData } from '../../models/types/get-account-stats-response';

import cls from './trading-platform-net-daily-chart.module.css';

interface TradingPlatformNetDailyChartProps {
    chartData: TradingPlatformAccountStatsChartData[];
    isLoading: boolean;
    isMobile?: boolean;
}

export const TradingPlatformNetDailyChart = memo(
    (props: TradingPlatformNetDailyChartProps) => {
        const { chartData, isLoading, isMobile = false } = props;

        const chartContainerRef = useRef<HTMLDivElement | null>(null);
        const chart = useRef<IChartApi | null>(null);

        const [theme] = useTheme();
        const [language] = useLanguage();

        const initialData = useMemo(
            () =>
                chartData.map(({ date, total_profit: value }) => ({
                    ...(value < 0 ? { color: '#FF4D4F' } : {}),
                    time: date,
                    value,
                })),
            [chartData],
        );

        useEffect(() => {
            if (!isLoading) {
                chart.current = createChart(chartContainerRef.current!, {
                    layout: {
                        ...chart.current?.options().layout,
                        fontFamily: "'Roboto', sans-serif",
                        background: {
                            type: ColorType.Solid,
                            color: 'transparent',
                        },
                    },
                    width: chartContainerRef.current!.clientWidth,
                    height: 210,
                    autoSize: true,
                    crosshair: {
                        horzLine: { visible: false },
                        vertLine: { visible: false },
                    },
                    timeScale: {
                        ...chart.current?.options().timeScale,
                        tickMarkFormatter: (time: Time) => time,
                    },
                    grid: {
                        ...chart.current?.options().grid,
                        vertLines: {
                            visible: false,
                        },
                    },
                    handleScroll: true,
                    handleScale: true,
                    leftPriceScale: {
                        ...chart.current?.options().leftPriceScale,
                        visible: true,
                        alignLabels: false,
                    },
                    rightPriceScale: {
                        visible: false,
                    },
                    localization: {
                        locale: 'en',
                    },
                });

                chart.current.timeScale().fitContent();

                const histogramSeries = chart.current.addHistogramSeries({
                    priceLineVisible: false,
                    lastValueVisible: false,
                    color: '#26a69a',
                });

                histogramSeries.setData(initialData);
            }

            return () => {
                if (!isLoading && chart.current) {
                    chart.current.remove();
                }
            };
        }, [initialData, isLoading]);

        useEffect(() => {
            const isDark = theme === THEME.dark;

            chart.current?.applyOptions({
                layout: {
                    background: {
                        type: ColorType.Solid,
                        color: 'transparent',
                    },
                    textColor: isDark
                        ? 'rgb(255 255 255 / 45%)'
                        : 'rgb(0 0 0 / 45%)',
                },
                grid: {
                    horzLines: {
                        color: isDark ? '#2A2E39' : '#F0F0F0',
                    },
                },
                leftPriceScale: {
                    borderColor: isDark ? '#2A2E39' : '#F0F0F0',
                },
                timeScale: {
                    borderColor: isDark ? '#2A2E39' : '#F0F0F0',
                },
            });
        }, [theme]);

        useEffect(() => {
            chart.current?.applyOptions({
                localization: {
                    locale: language,
                },
            });
        }, [language]);

        return (
            <div
                ref={chartContainerRef}
                className={cn(cls.root, { [cls.mobile]: isMobile })}
            >
                {isLoading && <Loader />}
            </div>
        );
    },
);

TradingPlatformNetDailyChart.displayName = 'TradingPlatformNetDailyChart';
